import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { FieldInput } from "@/components/FieldInput";
import { api, errMsg, fmtDateTime, ROLE_LABEL } from "@/lib/api";

const EMPTY = { masters: { dse: [], fl: [], schemes: [] } };

function UserDialog({ item, onClose, onSaved }) {
  const [v, setV] = useState(item ? { ...item, password: "" } : { role: "staff", active: true });
  const fields = [
    { name: "name", label: "Name", type: "text", required: true },
    { name: "email", label: "Email", type: "text" },
    { name: "mobile", label: "Mobile", type: "text" },
    { name: "role", label: "Role", type: "select", options: Object.entries(ROLE_LABEL), required: true },
    { name: "password", label: item ? "New password (optional)" : "Password (min 8)", type: "text", required: !item },
  ];
  const save = async () => {
    try {
      if (item) await api.put(`/users/${item.id}`, v);
      else await api.post("/users", v);
      toast.success("User saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="user-dialog">
        <DialogHeader><DialogTitle className="font-display">{item ? "Edit user" : "Add user"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          {fields.map((f) => <FieldInput key={f.name} f={f} value={v[f.name]} onChange={(x) => setV((s) => ({ ...s, [f.name]: x }))} masters={EMPTY.masters} testPrefix="user" />)}
          <div className="flex items-center gap-2">
            <Switch checked={!!v.active} onCheckedChange={(x) => setV((s) => ({ ...s, active: x }))} id="user-active" data-testid="user-active-switch" />
            <Label htmlFor="user-active">Active</Label>
          </div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="user-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetData() {
  const [open, setOpen] = useState(false);
  const [txt, setTxt] = useState("");
  const run = async () => {
    try {
      await api.post("/admin/reset-data", { confirm: txt });
      toast.success("Saara data zero ho gaya");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="reset-data-zone">
      <div>
        <p className="font-semibold text-rose-800">Reset all data</p>
        <p className="text-xs text-rose-700">Saari entries, DSE/FL/Party masters, bank accounts, audit logs aur doosre users delete honge. Sirf aapka admin login rahega. Undo nahi hoga.</p>
      </div>
      <Button variant="destructive" onClick={() => setOpen(true)} data-testid="reset-data-btn">Reset all data</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="reset-data-dialog">
          <DialogHeader><DialogTitle>Confirm reset</DialogTitle></DialogHeader>
          <Label htmlFor="reset-confirm">Confirm karne ke liye RESET type karein</Label>
          <input id="reset-confirm" className="border rounded-md px-3 py-2" value={txt} onChange={(e) => setTxt(e.target.value)} data-testid="reset-confirm-input" />
          <DialogFooter><Button variant="destructive" disabled={txt !== "RESET"} onClick={run} data-testid="reset-confirm-btn">Delete everything</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Users() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(undefined);
  const load = () => api.get("/users").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Users & Roles</h1>
          <span className="hint-tag mt-2">Admin · Accountant · Staff · View Only — open signup band hai</span>
        </div>
        <Button onClick={() => setEdit(null)} data-testid="add-user-btn"><Plus className="h-4 w-4" /> Add user</Button>
      </div>
      <div className="overflow-auto border border-slate-200 rounded-xl bg-white">
        <table className="dense-table" data-testid="users-table">
          <thead><tr><th>Name</th><th>Email</th><th>Mobile</th><th>Role</th><th>Status</th><th>Created</th><th /></tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="font-medium">{u.name}</td><td>{u.email}</td><td className="num">{u.mobile}</td>
                <td>{ROLE_LABEL[u.role]}</td>
                <td>{u.active ? <span className="text-emerald-700">Active</span> : <span className="text-slate-400">Inactive</span>}</td>
                <td className="num text-xs">{fmtDateTime(u.created_at)}</td>
                <td><button onClick={() => setEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-3.5 w-3.5 text-slate-500" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit !== undefined && <UserDialog item={edit} onClose={() => setEdit(undefined)} onSaved={load} />}
      <ResetData />
    </div>
  );
}
